import React, { useState, useMemo } from 'react';
import {
  AppBar,
  Avatar,
  Box,
  Button,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
} from '@mui/material';
import { useTenant } from '../../../contexts/TenantContext';
import { useAuth } from '../../../contexts/AuthContext';
import { useHeader } from '../../../contexts/HeaderContext';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import BadgeIcon from '@mui/icons-material/Badge';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import OverviewHeaderActions from '../../overview/components/OverviewHeaderActions';

const PAGE_TITLES: Record<string, string> = {
  '/overview': 'Property Listings',
};

interface SaasHeaderProps {
  // Optional prop to override the logo
  logoOverride?: string;
  // Optional prop to show additional navigation items
  navigationItems?: React.ReactNode;
  // Optional prop to control header elevation
  elevation?: number;
  // Optional prop for custom height
  height?: number | string;
}

const SaasHeader: React.FC<SaasHeaderProps> = ({
  logoOverride,
  navigationItems,
  elevation = 1,
  height = 64,
}) => {
  const { tenant, getConfigValue } = useTenant();
  const { session, logout, isAuthenticated } = useAuth();
  const { headerData } = useHeader();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const open = Boolean(anchorEl);

  // Get logo from config or use override
  const logoUrl = logoOverride || getConfigValue<string>('COMPANY_LOGO', '');
  const companyName =
    getConfigValue<string>('COMPANY_NAME', '') ||
    tenant.charAt(0).toUpperCase() + tenant.slice(1);

  const pageTitle = useMemo(() => {
    const pathname = window.location.pathname;

    const pathWithoutTenant = pathname.replace(new RegExp(`^/${tenant}`), '');

    // Only return title if explicitly defined
    return PAGE_TITLES[pathWithoutTenant] || null;
  }, [tenant]);

  // Get user info from session
  const userFullName = session?.user
    ? `${session.user.firstName} ${session.user.lastName}`
    : 'User';
  const userMlsId = session?.user?.memberMlsId || '';
  const userEmail = session?.user?.email || '';
  const userInitials = session?.user
    ? `${session.user.firstName?.charAt(0)}${session.user.lastName?.charAt(0)}`.toUpperCase()
    : 'U';

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleMenuClose();
    await logout();
  };

  return (
    <AppBar
      position="sticky"
      elevation={elevation}
      sx={{
        backgroundColor: 'background.paper',
        borderBottom: '1px solid',
        borderBottomColor: 'grey.200',
        height: height,
      }}
    >
      <Toolbar
        sx={{
          height: '100%',
          minHeight: `${height}px !important`,
          px: { xs: 2, sm: 3, md: 4 },
        }}
      >
        {/* Logo Section */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexShrink: 0,
            mr: { xs: 2, md: 4 },
          }}
        >
          {logoUrl ? (
            <Box
              component="img"
              src={logoUrl}
              alt={companyName}
              sx={{
                height: { xs: 32, md: 40 },
                maxWidth: { xs: 120, md: 160 },
                objectFit: 'contain',
                cursor: 'pointer',
              }}
              onClick={() => (window.location.href = '/')}
            />
          ) : (
            <Typography
              variant="h5"
              sx={{
                color: 'primary.main',
                fontWeight: 700,
                cursor: 'pointer',
                fontSize: { xs: '1.25rem', md: '1.5rem' },
                '&:hover': {
                  color: 'primary.dark',
                },
                transition: 'color 0.2s ease',
              }}
              onClick={() => (window.location.href = '/')}
            >
              {companyName}
            </Typography>
          )}
        </Box>

        {/* Page Title - only shown if defined */}
        {pageTitle && (
          <Typography
            variant="h6"
            sx={{
              color: 'text.secondary',
              fontWeight: 600,
              fontSize: { xs: '16px', md: '24px' },
              display: { xs: 'none', sm: 'block' },
              mr: 2,
            }}
          >
            {pageTitle}
          </Typography>
        )}

        {/* Navigation Items (if provided) */}
        {navigationItems && (
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              alignItems: 'center',
              gap: 2,
              flex: 1,
            }}
          >
            {navigationItems}
          </Box>
        )}

        {/* Spacer */}
        <Box sx={{ flexGrow: 1 }} />

        {/* Overview-specific content */}
        {(headerData.showAddListingButton ||
          headerData.propertiesCount !== undefined) && (
          <OverviewHeaderActions />
        )}

        {/* User Section */}
        {isAuthenticated && session ? (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Button
              onClick={handleMenuOpen}
              endIcon={<ExpandMoreIcon />}
              sx={{
                textTransform: 'none',
                color: 'text.primary',
                '&:hover': {
                  backgroundColor: 'grey.100',
                },
                borderRadius: 3,
                px: { xs: 1, md: 2 },
              }}
            >
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: 'secondary.main',
                  fontSize: '0.875rem',
                  mr: { xs: 0.5, md: 1 },
                }}
              >
                {userInitials}
              </Avatar>
              <Box
                sx={{
                  display: { xs: 'none', sm: 'block' },
                  textAlign: 'left',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: 'text.primary',
                    lineHeight: 1.2,
                  }}
                >
                  {userFullName}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.75rem',
                  }}
                >
                  MLS ID: {userMlsId}
                </Typography>
              </Box>
            </Button>

            {/* User Dropdown Menu */}
            <Menu
              anchorEl={anchorEl}
              open={open}
              onClose={handleMenuClose}
              onClick={handleMenuClose}
              PaperProps={{
                elevation: 3,
                sx: {
                  mt: 1.5,
                  minWidth: 240,
                  borderRadius: 1,
                  '& .MuiAvatar-root': {
                    width: 32,
                    height: 32,
                    ml: -0.5,
                    mr: 1,
                  },
                },
              }}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
              {/* User Info Header */}
              <Box sx={{ px: 2, py: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {userFullName}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {userEmail}
                </Typography>
              </Box>

              <Divider />

              {/* MLS ID Display */}
              <MenuItem disabled>
                <ListItemIcon>
                  <BadgeIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>
                  <Typography variant="body2" sx={{ marginTop: 1 }}>
                    MLS ID: {userMlsId}
                  </Typography>
                </ListItemText>
              </MenuItem>

              <Divider />

              {/* Logout Option */}
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <LogoutIcon fontSize="small" sx={{ color: 'error.main' }} />
                </ListItemIcon>
                <ListItemText>
                  <Typography sx={{ color: 'error.main' }}>Logout</Typography>
                </ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        ) : (
          // Not authenticated - show login button or placeholder
          <Button
            variant="contained"
            color="primary"
            onClick={() => (window.location.href = '/')}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
            }}
          >
            Sign In
          </Button>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default SaasHeader;
